import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Badge, Button } from '@goodday-clone/ui'
import { Download, Loader2, Mic, Play, Trash2, Upload } from 'lucide-react'
import { toast } from 'react-hot-toast'
import { isSuperAdminOrOwner, ROLES } from '@jira-clone/shared-types'
import { useAuth } from '@/context/AuthContext'
import { getApiErrorMessage } from '@/shared/utils/apiError'
import { fetchRecordingBlob, useLeadCallRecordings, type LeadCallRecordingItem } from '@/shared/hooks/useLeadActivity'

// Call recordings attached to a lead. The audio lives in the database and the
// API hands it to whoever can open the lead, so this panel follows the lead:
// uploading needs the lead's edit rights, and a recording is only taken back
// out by the person who put it there or by a sales manager above them.

function humanSize(bytes: number): string {
  if (!bytes) return '0 KB'
  const mb = bytes / (1024 * 1024)
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`
}

interface RowProps {
  leadId: string
  item: LeadCallRecordingItem
  canDelete: boolean
  onDelete: (id: string) => Promise<void>
}

function RecordingRow({ leadId, item, canDelete, onDelete }: RowProps) {
  const [url, setUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const created = useRef<string | null>(null)

  useEffect(
    () => () => {
      if (created.current) URL.revokeObjectURL(created.current)
      created.current = null
    },
    []
  )

  // The bytes are only pulled once, and only for the recording someone asked
  // for, so opening a lead does not download every call on it.
  const load = useCallback(async () => {
    if (created.current) return created.current
    setLoading(true)
    try {
      const blob = await fetchRecordingBlob(leadId, item.id)
      const href = URL.createObjectURL(blob)
      created.current = href
      setUrl(href)
      return href
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'open the recording'))
      return null
    } finally {
      setLoading(false)
    }
  }, [leadId, item.id])

  const download = async () => {
    const href = await load()
    if (!href) return
    const link = document.createElement('a')
    link.href = href
    link.download = item.filename
    document.body.appendChild(link)
    link.click()
    link.remove()
  }

  return (
    <li className="space-y-2 rounded-lg border border-border p-2.5">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-xs font-semibold text-foreground" title={item.filename}>
            {item.filename}
          </p>
          <p className="text-[11px] text-muted-foreground">
            {humanSize(item.sizeBytes)}
            {item.uploadedBy ? ` · ${item.uploadedBy.name}` : ''}
            {item.createdAt ? ` · ${new Date(item.createdAt).toLocaleString()}` : ''}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Button type="button" size="sm" variant="ghost" className="h-7 gap-1.5 text-xs" onClick={load} disabled={loading}>
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
            Listen
          </Button>
          <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={download} disabled={loading} aria-label={`Download ${item.filename}`}>
            <Download className="h-3.5 w-3.5" />
          </Button>
          {canDelete && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-7 w-7 p-0 text-destructive"
              onClick={() => onDelete(item.id)}
              aria-label={`Delete ${item.filename}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>
      {url && <audio controls src={url} aria-label={`Play ${item.filename}`} className="h-9 w-full" />}
    </li>
  )
}

export function LeadRecordingsPanel({ leadId }: { leadId?: string }) {
  const { user, hasPermission } = useAuth()
  const canUpload = hasPermission('LEADS', 'EDIT')
  const isManager = isSuperAdminOrOwner(user?.role) || user?.role === ROLES.ADMIN
  const { recordings, loading, uploadRecording, deleteRecording } = useLeadCallRecordings(leadId)
  const [busy, setBusy] = useState(false)
  const fileInput = useRef<HTMLInputElement>(null)

  const pickFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file || !leadId) return
    setBusy(true)
    try {
      await uploadRecording(file)
      toast.success(`${file.name} added to this lead`)
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'upload the recording'))
    } finally {
      setBusy(false)
    }
  }

  const remove = async (id: string) => {
    if (!window.confirm('Delete this recording? The audio cannot be recovered.')) return
    try {
      await deleteRecording(id)
      toast.success('Recording deleted')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'delete the recording'))
    }
  }

  return (
    <div className="space-y-3 pt-3 border-t border-border/60">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="p-1.5 rounded-lg bg-rose-500/10 text-rose-600 dark:text-rose-400">
            <Mic className="h-4 w-4" />
          </span>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-bold text-foreground">Call Recordings</h3>
              <Badge variant="outline" className="text-[10px] font-mono font-bold bg-rose-50 text-rose-700 border-rose-300 dark:bg-rose-950/40 dark:text-rose-300">
                {recordings.length} saved
              </Badge>
            </div>
            <p className="text-[11px] text-muted-foreground">Audio of calls with this lead - up to 10 MB each, heard by whoever can open the lead</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {canUpload && (
            <>
              <input
                ref={fileInput}
                type="file"
                accept="audio/*,.mp3,.wav,.m4a,.ogg,.opus,.webm,.flac,.aiff,.amr"
                className="sr-only"
                aria-label="Call recording"
                onChange={pickFile}
              />
              <Button type="button" size="sm" variant="outline" className="h-7 gap-1.5 text-xs" onClick={() => fileInput.current?.click()} disabled={busy || !leadId}>
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                Upload recording
              </Button>
            </>
          )}
        </div>
      </div>

      {loading ? (
        <p className="text-xs text-muted-foreground">Loading recordings…</p>
      ) : recordings.length === 0 ? (
        <p className="text-xs text-muted-foreground">No recording uploaded for this lead yet.</p>
      ) : (
        <ul className="space-y-2">
          {recordings.map((item) => (
            <RecordingRow
              key={item.id}
              leadId={leadId!}
              item={item}
              canDelete={isManager || item.uploadedBy?.id === user?.id}
              onDelete={remove}
            />
          ))}
        </ul>
      )}
    </div>
  )
}
